import { useState, Dispatch, SetStateAction } from 'react';

import { ProfileReservation } from '@assets/icons';
import { DateType, isDayFromThePast } from '@utils/dateUtil';
import ResponsiveSearchDrawerItem from '@components/views/components/listing/drawer/ResponsiveSearchDrawerItem';
import ResponsiveApplyButton from '@components/views/components/listing/drawer/ResponsiveApplyButton/ResponsiveApplyButton';
import ResponsiveSearchInputDrawer from '@components/views/components/listing/drawer/ResponsiveSearchInputDrawer/ResponsiveSearchInputDrawer';
import { FlightSearchInputDrawerType } from '@components/views/flight/types';
import { DayPickerRangeControllerProps } from '@components/DatePicker/DateRangePicker/DateRangePicker';
import { disablePastHotelDatePicker } from '@components/views/landing/components/booker/common/bookerUtils';
import { CalendarHolidaysViewModel } from '@api/v3';
import ResponsiveDateRangePicker from '@components/DatePicker/DateRangePicker/ResponsiveDateRangePicker';

import {
  getDateRangePickerPlaceholder,
  isFlightReturnDateOutsideRange,
} from './utils';

const searchLabel = 'Gidiş-Dönüş Tarihiniz?';

const ResponsiveDateRangeSelectDrawer = ({
  startDate,
  endDate,
  onDatesChange,
  title,
  subTitle,
  placeholder,
  drawerTitle,
  arrowIcon,
  isPreviousHotelDatePicker,
  monthsRange,
  isHoliday,
  initialData,
  setDefaultEndDate,
  setDefaultStarDate,
}: Pick<
  DayPickerRangeControllerProps,
  'startDate' | 'endDate' | 'onDatesChange'
> &
  FlightSearchInputDrawerType & {
    isPreviousHotelDatePicker?: boolean;
    monthsRange?: number;
    isHoliday?: boolean;
    initialData?: CalendarHolidaysViewModel;
    setDefaultEndDate?: Dispatch<SetStateAction<string>>;
    setDefaultStarDate?: Dispatch<SetStateAction<string>>;
  }) => {
  return (
    <ResponsiveSearchInputDrawer
      icon={ProfileReservation}
      title={title}
      subTitle={subTitle}
      placeholder={placeholder}
      drawerTitle={drawerTitle}
      arrowIcon={arrowIcon}
      booker={true}
    >
      {(hideModal) => (
        <ResponsiveDateRangeSelect
          initialData={initialData}
          startDate={startDate}
          endDate={endDate}
          isHoliday={isHoliday}
          setDefaultEndDate={setDefaultEndDate}
          setDefaultStarDate={setDefaultStarDate}
          onDatesChange={onDatesChange}
          hideModal={hideModal}
          isPreviousHotelDatePicker={isPreviousHotelDatePicker}
          monthsRange={monthsRange}
        />
      )}
    </ResponsiveSearchInputDrawer>
  );
};

const ResponsiveDateRangeSelect = ({
  startDate,
  endDate,
  onDatesChange,
  hideModal,
  isPreviousHotelDatePicker,
  monthsRange,
  isHoliday,
  initialData,
  setDefaultEndDate,
  setDefaultStarDate,
}: Pick<
  DayPickerRangeControllerProps,
  'startDate' | 'endDate' | 'onDatesChange'
> & {
  hideModal: () => void;
  isPreviousHotelDatePicker?: boolean;
  monthsRange?: number;
  isHoliday?: boolean;
  initialData?: CalendarHolidaysViewModel;
  setDefaultEndDate?: Dispatch<SetStateAction<string>>;
  setDefaultStarDate?: Dispatch<SetStateAction<string>>;
}) => {
  const [localStartDate, setLocalStartDate] = useState<DateType | null>(
    startDate
  );
  const [localEndDate, setLocalEndDate] = useState<DateType | null>(endDate);

  const handleDatesChange = ({
    startDate,
    endDate,
  }: {
    startDate: DateType | null;
    endDate: DateType | null;
  }) => {
    setLocalStartDate(startDate);
    setLocalEndDate(endDate);
  };

  const localSubTitle = getDateRangePickerPlaceholder(
    localStartDate,
    localEndDate
  );
  const startDateString = localStartDate?.isValid()
    ? localStartDate.utc().local().format('DD.MM.YYYY') // UTC'yi önce UTC'ye çevir, sonra local() ile yerel saate al
    : '';

  const endDateFormatString = localEndDate?.isValid()
    ? localEndDate.utc().local().format('DD.MM.YYYY') // UTC'yi önce UTC'ye çevir, sonra local() ile yerel saate al
    : '';
  const onSubmit = () => {
    if (localStartDate && localEndDate) {
      setDefaultEndDate?.(endDateFormatString);
      setDefaultStarDate?.(startDateString);
      onDatesChange({ startDate: localStartDate, endDate: localEndDate });
      hideModal();
    }
  };

  return (
    <>
      <ResponsiveSearchDrawerItem
        icon={ProfileReservation}
        title={searchLabel}
        subTitle={localSubTitle}
        placeholder='Gidiş - Dönüş Tarihleri'
      />

      <ResponsiveDateRangePicker
        initialData={initialData}
        minimumNights={0}
        startDate={localStartDate}
        endDate={localEndDate}
        onDatesChange={handleDatesChange}
        isHoliday={isHoliday}
        isOutsideRange={(day) => {
          return isDayFromThePast(day)
            ? true
            : isPreviousHotelDatePicker
            ? disablePastHotelDatePicker(day, localStartDate, localEndDate)
            : isFlightReturnDateOutsideRange(day, localStartDate, monthsRange);
        }}
      />
      <ResponsiveApplyButton onClick={onSubmit} />
    </>
  );
};

export default ResponsiveDateRangeSelectDrawer;
